import type { CSSProperties } from "react";
import type { LibraryNode } from "../../../lib/types";
import { isPatternMemory } from "../../hooks/use-agent-learnings";

/**
 * Sprint 21 — a small chip showing where a memory came from (its provenance),
 * folding in the Sprint 20a polish that was deferred. Source is inferred from
 * the memory's signals: an explicit `memory_source_kind`, else a task link
 * (⇒ retrospective/pattern), else the creating host (⇒ agent), else user.
 */

type Provenance = "user" | "agent" | "retrospective" | "pattern" | "activity" | "routine_run";

const META: Record<Provenance, { icon: string; label: string; color: string }> = {
  user: { icon: "ti-user", label: "user", color: "var(--color-text-tertiary)" },
  agent: { icon: "ti-robot", label: "agent", color: "var(--color-text-info)" },
  retrospective: { icon: "ti-history", label: "retrospective", color: "var(--color-text-info)" },
  pattern: { icon: "ti-repeat", label: "pattern", color: "var(--color-text-success, var(--color-text-info))" },
  activity: { icon: "ti-activity", label: "activity", color: "var(--color-text-tertiary)" },
  routine_run: { icon: "ti-repeat", label: "routine", color: "var(--color-text-tertiary)" },
};

function provenanceOf(node: LibraryNode): Provenance {
  if (isPatternMemory(node)) return "pattern";
  const kind = node.memory_source_kind;
  if (kind && kind in META) return kind as Provenance;
  if (node.memory_source_task_id) return "retrospective";
  if (node.created_via) return "agent";
  return "user";
}

const chipStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 3,
  fontSize: 10,
  fontWeight: 600,
  lineHeight: 1.4,
  padding: "1px 6px",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-secondary)",
  whiteSpace: "nowrap",
};

export function MemoryProvenanceChip({ node }: { node: LibraryNode }) {
  const kind = provenanceOf(node);
  const meta = META[kind];
  return (
    <span style={{ ...chipStyle, color: meta.color }} title={`Source: ${meta.label}`}>
      <i className={`ti ${meta.icon}`} style={{ fontSize: 11 }} aria-hidden="true" />
      {meta.label}
    </span>
  );
}
